/**
 * Take Away — τι κάνει ο ταμίας όταν η κάρτα/IRIS δεν πέρασε (αποφάσεις ιδιοκτήτη 12/10/2026).
 *
 *  failed    = ΣΙΓΟΥΡΑ δεν χρεώθηκε: τρία μεγάλα κουμπιά στην ΙΔΙΑ παραγγελία —
 *              «Ξανά κάρτα» (ή «Ξανά IRIS»), «Μετρητά», «Ακύρωση».
 *  uncertain = δεν ξέρουμε αν χρεώθηκε: ούτε νέα χρέωση ούτε μετρητά. «Έλεγχος ξανά» (ρωτά τον
 *              πάροχο) και οδηγία να κληθεί ο υπεύθυνος· το «Κλείσιμο» αφήνει την παραγγελία όπως
 *              είναι, για τον υπεύθυνο, χωρίς να αγγίζει χρήματα.
 *
 * Σκέτη παρουσίαση: τις ενέργειες τις κάνει ο καλών (TakeAway), ώστε η ίδια οθόνη να μπαίνει σε όποια
 * εμφάνιση του ταμείου (κλασική / νέα).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Banknote, CreditCard, RefreshCw, X } from 'lucide-react';

interface Props {
  mode: 'failed' | 'uncertain';
  /** Εμφάνιση ταμείου: ίδια κουμπιά και κείμενα, η νέα με τα ουδέτερα χρώματα και ΕΝΑ μπλε (brand). */
  look?: 'classic' | 'v2';
  method: 'card' | 'iris';
  /** Μόνο «Ακύρωση» (π.χ. το ποσό δεν ταιριάζει με την παραγγελία). */
  cancelOnly?: boolean;
  busy?: boolean;
  onRetryCard: () => void;
  onPayCash: () => void;
  onCancelOrder: () => void;
  onCheckAgain: () => void;
  onLeaveForManager: () => void;
}

const big = 'w-full min-h-[64px] px-4 rounded-xl text-lg font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed';

const CardFailureActions: React.FC<Props> = ({
  mode, look = 'classic', method, cancelOnly = false, busy = false,
  onRetryCard, onPayCash, onCancelOrder, onCheckAgain, onLeaveForManager,
}) => {
  const { t } = useTranslation(['takeaway']);
  const v2 = look === 'v2';
  const primary = v2 ? 'bg-brand text-brand-fg hover:brightness-110'
    : method === 'iris' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white';
  const cash = v2 ? 'bg-surface-1 border border-line text-t1 hover:bg-surface-3' : 'bg-green-600 hover:bg-green-700 text-white';
  const cancel = v2 ? 'border border-line text-red-600 dark:text-red-400 hover:bg-surface-3'
    : 'border-2 border-red-400 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-500/10';
  const warnBox = v2 ? 'border border-line bg-surface-1' : 'border-2 border-amber-500/60 bg-amber-500/10';

  if (mode === 'uncertain') {
    return (
      <div className="space-y-3 text-left" data-testid="card-uncertain">
        <div className={`flex gap-3 rounded-xl p-4 ${warnBox}`}>
          <AlertTriangle className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <div className="font-bold text-t1">{t('takeaway:cardUncertainTitle')}</div>
            <div className="text-sm text-t2 mt-1">{t('takeaway:cardUncertainHint')}</div>
          </div>
        </div>
        <button type="button" onClick={onCheckAgain} disabled={busy} className={`${big} ${v2 ? primary : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
          <RefreshCw className={`h-6 w-6 ${busy ? 'animate-spin' : ''}`} />
          {t('takeaway:cardCheckAgain')}
        </button>
        <button
          type="button"
          onClick={onLeaveForManager}
          disabled={busy}
          className="w-full py-2 text-sm text-t3 underline underline-offset-4 hover:text-t1 disabled:opacity-50"
        >
          {t('takeaway:cardLeaveForManager')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="card-failed-actions">
      {!cancelOnly && (
        <p className="text-sm text-t2">{t('takeaway:cardFailedSameOrder')}</p>
      )}
      {!cancelOnly && (
        <button type="button" onClick={onRetryCard} disabled={busy} className={`${big} ${primary}`}>
          <CreditCard className="h-6 w-6" />
          {method === 'iris' ? t('takeaway:irisRetry') : t('takeaway:cardRetry')}
        </button>
      )}
      {!cancelOnly && (
        <button type="button" onClick={onPayCash} disabled={busy} className={`${big} ${cash}`}>
          <Banknote className="h-6 w-6" />
          {t('takeaway:cardPayCash')}
        </button>
      )}
      <button
        type="button"
        onClick={onCancelOrder}
        disabled={busy}
        className={`${big} ${cancel}`}
      >
        <X className="h-6 w-6" />
        {t('takeaway:cardCancelOrder')}
      </button>
    </div>
  );
};

export default CardFailureActions;
